import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, ArrowLeft, MapPin, Phone, Star, Navigation } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DoctorFinder = () => {
  const navigate = useNavigate();
  const [location, setLocation] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);

  const searchDoctors = async () => {
    if (!location.trim()) return;

    setSearching(true);
    try {
      const response = await axios.post(`${API}/doctor/search`, {
        location: location,
        specialization: specialization || null
      }, {
        withCredentials: true
      });
      setResults(response.data);
    } catch (error) {
      console.error('Error searching doctors:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchDoctors();
    }
  };

  return (
    <div className="dashboard-container" data-testid="doctor-finder-page">
      {/* Navbar */}
      <nav className="navbar">
        <div className="navbar-brand">
          <Activity size={28} />
          MedPlexity
        </div>
        <div className="navbar-menu">
          <button 
            className="nav-link" 
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={18} /> Back
          </button>
        </div>
      </nav>

      <div className="content-section">
        <h2 className="section-title" data-testid="page-title">
          <MapPin size={32} /> Find Doctors & Hospitals
        </h2>
        <p style={{ color: '#78909c', marginBottom: '2rem' }}>
          Discover nearby healthcare providers based on your location and needs
        </p>

        {/* Search Form */}
        <div className="symptom-input-area" style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#546e7a', fontWeight: '500' }}>
                Location *
              </label>
              <input
                type="text"
                className="search-input"
                placeholder="Enter your city or area..."
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                onKeyPress={handleKeyPress}
                data-testid="location-input"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#546e7a', fontWeight: '500' }}>
                Specialization (Optional)
              </label>
              <input
                type="text"
                className="search-input"
                placeholder="E.g., Cardiologist, Neurologist..."
                value={specialization}
                onChange={(e) => setSpecialization(e.target.value)}
                onKeyPress={handleKeyPress}
                data-testid="specialization-input"
              />
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <button 
              className="action-btn btn-primary" 
              onClick={searchDoctors}
              disabled={searching || !location.trim()}
              data-testid="search-button"
            >
              <MapPin size={20} /> {searching ? 'Searching...' : 'Search Doctors'}
            </button>
          </div>
        </div>

        {/* Notice */}
        {results && (
          <div style={{ 
            background: results.is_mocked ? '#fff3e0' : '#e3f2fd', 
            padding: '1rem', 
            borderRadius: '8px', 
            marginBottom: '2rem',
            border: results.is_mocked ? '1px solid #ffb74d' : '1px solid #64b5f6',
            color: results.is_mocked ? '#e65100' : '#01579b'
          }}>
            <strong>{results.is_mocked ? 'Note:' : 'Info:'}</strong> {results.source || 'AI-generated doctor recommendations based on your location'}
          </div>
        )}

        {/* Results */}
        {results && results.doctors && results.doctors.length > 0 && (
          <div>
            <h3 style={{ color: '#0277bd', marginBottom: '1.5rem' }} data-testid="results-title">
              Found {results.doctors.length} healthcare providers near {results.location}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {results.doctors.map((doctor, index) => (
                <div key={doctor.id} className="doctor-card" data-testid={`doctor-card-${index}`}>
                  <div className="doctor-info">
                    <h4 className="doctor-name" data-testid={`doctor-name-${index}`}>{doctor.name}</h4>
                    <p className="doctor-spec" data-testid={`doctor-spec-${index}`}>{doctor.specialization}</p>
                    <p className="doctor-hospital">{doctor.hospital}</p>
                    {doctor.address && (
                      <p style={{ color: '#78909c', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                        📍 {doctor.address}
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.75rem', color: '#78909c', fontSize: '0.9rem', flexWrap: 'wrap' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <MapPin size={16} /> {doctor.distance}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Star size={16} color="#ffa726" fill="#ffa726" /> {doctor.rating}
                      </span>
                      <span>{doctor.consultation_type}</span>
                      {doctor.years_experience && (
                        <span>🩺 {doctor.years_experience} years exp.</span>
                      )}
                    </div>
                  </div>
                  <div className="doctor-actions">
                    <button 
                      className="action-btn btn-primary" 
                      onClick={() => window.open(`tel:${doctor.phone}`, '_self')}
                      data-testid={`call-btn-${index}`}
                    >
                      <Phone size={18} /> Call
                    </button>
                    <button 
                      className="action-btn btn-secondary"
                      onClick={() => {
                        const address = doctor.address || `${doctor.hospital}, ${results.location}`;
                        window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
                      }}
                      data-testid={`navigate-btn-${index}`}
                    >
                      <Navigation size={18} /> Navigate
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {results && results.doctors && results.doctors.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#78909c' }}>
            No doctors found matching your criteria. Try adjusting your search.
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorFinder;
